import type { SupportLogEntry, SupportLoggerConfig } from './support-logging.types';

export let logger: SupportLogger | undefined;

export function initSupportLogging(config: SupportLoggerConfig): void {
  logger?.destroy();
  logger = new SupportLogger(config);
}

const MAX_QUEUE_SIZE = 100;
const MAX_RETRY_COUNT = 3;
const DEFAULT_FLUSH_THRESHOLD = 80; // 80% of BE batch limit — flush early, reset timer

export class SupportLogger {
  private readonly queue: SupportLogEntry[] = [];
  private readonly config: SupportLoggerConfig;
  private readonly flushThreshold: number;
  private intervalId: ReturnType<typeof setInterval> | undefined;
  private retryCount = 0;
  private isFlushing = false;

  constructor(config: SupportLoggerConfig) {
    this.config = config;
    this.flushThreshold = config.flushThreshold ?? DEFAULT_FLUSH_THRESHOLD;
    this.intervalId = setInterval(() => {
      void this.flush();
    }, config.intervalMs);
  }

  private serializeError(error: unknown): unknown {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
        errorType: error.name === 'TypeError' && error.message.toLowerCase().includes('fetch')
          ? 'api_error'
          : 'js_error',
      };
    }
    return { raw: String(error), errorType: 'unknown' };
  }

  log(entry: SupportLogEntry): void {
    const queueEntry: SupportLogEntry = {
      ...entry,
      clientTimestamp: entry.clientTimestamp ?? new Date().toISOString(),
      ...(entry.error !== undefined ? { error: this.serializeError(entry.error) } : {}),
      ...(entry.extra !== undefined ? { extra: entry.extra } : {}),
    };
    if (this.queue.length >= MAX_QUEUE_SIZE) {
      void this.flush(); // attempt to drain before losing data
      this.queue.shift(); // drop oldest only after flush is in flight
    }
    this.queue.push(queueEntry);

    // Threshold flush: approaching BE batch limit — flush immediately and reset timer.
    if (this.queue.length >= this.flushThreshold) {
      this.resetTimer();
      void this.flush();
    }
  }

  logError(entry: Omit<SupportLogEntry, 'severity'> & { severity?: SupportLogEntry['severity'] }): void {
    this.log({ severity: 'error', ...entry });
  }

  private resetTimer(): void {
    clearInterval(this.intervalId);
    this.intervalId = setInterval(() => {
      void this.flush();
    }, this.config.intervalMs);
  }

  async flush(): Promise<void> {
    if (this.isFlushing || this.queue.length === 0) return;
    this.isFlushing = true;

    const batch = this.queue.slice(0, MAX_QUEUE_SIZE);

    try {
      const response = await this.config.postFn(batch);

      if (response.status === 401) {
        this.queue.splice(0, batch.length);
        this.retryCount = 0;
        return;
      }

      if (!response.ok) {
        this.retryCount += 1;
        if (this.retryCount >= MAX_RETRY_COUNT) {
          this.queue.splice(0, batch.length);
          this.retryCount = 0;
        }
        return;
      }

      this.queue.splice(0, batch.length);
      this.retryCount = 0;
    } catch {
      // Network error — treat same as 5xx: retain and retry
      this.retryCount += 1;
      if (this.retryCount >= MAX_RETRY_COUNT) {
        this.queue.splice(0, batch.length);
        this.retryCount = 0;
      }
    } finally {
      this.isFlushing = false;
    }
  }

  flushOnPageHide(): void {
    if (this.queue.length === 0) return;
    this.config.postBeaconFn(this.queue.slice(0, MAX_QUEUE_SIZE));
  }

  destroy(): void {
    void this.flush();
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }
}
